import styled from '@emotion/styled';
import { sendTTUEvents } from '@events/sendAnalytics';
import {
  TTU_FAB_CLICK,
  TTU_NUMBER_CLICKED,
  TTU_POP_UP_CLICK
} from '@constants/segment';
import {
  getIsRepeatUser,
  getTTUIsGoalEnabled,
  getTTUShowData
} from '@stores/selectors/ttuSelectors';

import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import Dynamic from '@base/Dynamic/Dynamic';
import H4 from '@base/H4/H4';
import { evtNameCategoryMap, inboundCallNumber } from '@constants/index';
import P1 from '@base/P1/P1';
import Button from '@base/Button/Button';
import useAccessControl from '@api/hooks/auth/useAccessControl';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMe, getIsPartner } from '@api/selectors/auth';
import useTTUStore from '@stores/ttu';
import Call from '@icon/Call';
import H6 from '@base/H6/H6';
import { segment } from '@actions/analytics';
import { emptyObject } from '@constants/empty';
import { useCallback } from 'react';
import useSelectedGoal from '@api/hooks/topology/useSelectedGoal';
import useGeoLocation from '@api/hooks/useGeoLocation';
import useConstants from '@api/hooks/useConstants';
import { isEmailLoginCountry } from '@api/selectors/constants';

const Image = Dynamic(() => import('@base/Image'));
const Close = Dynamic(() => import('@icon/Close'));

const StyledH4 = styled(H4)`
  padding-right: ${Spacings.SPACING_27B};
`;

const StyledP2 = styled.div`
  margin-top: ${Spacings.SPACING_2B};
  color: var(--color-text-primary);
  font-size: ${Fonts.H6};
  line-height: 20px;
  padding-right: ${Spacings.SPACING_30B};
`;

const StyledButton = styled(Button)`
  margin-top: ${Spacings.SPACING_7B};
`;

const TTUIllustrationContainer = styled.div`
  position: absolute;
  height: 108px;
  width: 35%;
  right: 0;
  top: ${Spacings.SPACING_6B};

  ${mediaQuery.uptoTablet} {
    height: 100px;
    width: 35%;
    bottom: 0;
  }
`;

const TTUIllustration = styled(Image)`
  height: 100%;
`;

const TTUWidget = styled.div`
  background-color: var(--color-text-primary);
  width: 64px;
  height: 64px;
  position: fixed;
  bottom: ${Spacings.SPACING_26B};
  right: ${Spacings.SPACING_6B};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 300;
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const TTUToolTip = styled.div`
  width: 360px;
  height: ${(props) => (props.$isUs ? '306px' : '282px')};
  position: fixed;
  right: ${Spacings.SPACING_6B};
  padding: ${Spacings.SPACING_6B};
  bottom: 185px;
  border-radius: ${Spacings.SPACING_2B};
  background: linear-gradient(0deg, var(--color-base-1), var(--color-base-1));
  box-shadow: ${boxShadowGenerator()};
`;

const CallDiv = styled.div`
  display: flex;
  margin-top: ${Spacings.SPACING_7B};
  align-items: center;
  justify-content: center;
`;

const CallLabel = styled(P1)`
  font-size: ${Fonts.H7};
  font-weight: 700;
  line-height: 150%;
  color: var(--color-text-secondary);
`;

const Phone = styled.a`
  display: flex;
  width: fit-content;
  margin-left: 6px;
`;

function TTUWidgetRenderer({
  showTTUTooltip,
  setShowTTUTooltip,
  setShowTTU,
  ttuTriggerSource,
  setTTUTriggerSource,
  hideIntercom,
  ttuOptions: {
    heading,
    overrideGoalEnabled,
    eventProps = emptyObject,
    isOffline = false
  } = emptyObject
}) {
  const me = useMyInfo(getMe);
  const { selectedGoal } = useSelectedGoal();
  const { accessControl } = useAccessControl({ goalUID: selectedGoal?.uid });
  const isPartner = getIsPartner(me, accessControl);
  const isRepeatUser = useTTUStore(getIsRepeatUser);
  const ttuGoalEnabled = useTTUStore(getTTUIsGoalEnabled);
  const ttuShowData = useTTUStore(getTTUShowData);
  const { geoLocationData: { countryCode } = {} } = useGeoLocation();
  const isEmailLoginFlow = useConstants(isEmailLoginCountry(countryCode));
  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);

  const onPhoneNumberClick = useCallback(() => {
    segment.track(
      TTU_NUMBER_CLICKED,
      {
        inbound_phone_number: inboundCallNumber,
        ...eventProps
      },
      evtNameCategoryMap.WITHOUT_CATEGORY
    );
  }, [eventProps]);

  if (
    (ttuGoalEnabled || overrideGoalEnabled) &&
    (!ttuShowData?.state || ttuShowData?.state === 0) &&
    !hideIntercom &&
    !me.isEducator &&
    !isPartner
  ) {
    return (
      <TTUWidget
        onClick={() => {
          if (!showTTUTooltip) {
            sendTTUEvents(
              TTU_FAB_CLICK,
              {
                user_ttu_status: isRepeatUser ? 'Repeat' : 'New',
                last_primary_source_section: 'FAB icon',
                ...eventProps
              },
              me.uid,
              me.username,
              selectedGoal.uid,
              selectedGoal.name
            );
            setTTUTriggerSource('FAB icon');
          }
          setShowTTUTooltip(!showTTUTooltip);
        }}
      >
        {showTTUTooltip ? (
          <Close color="var(--color-base-0)" size="24px" />
        ) : (
          <Image
            src={isDarkModeOn ? 'ttu-dark.svg' : 'ttu.svg'}
            height={24}
            width={24}
            alt="ttu"
          />
        )}
        {showTTUTooltip && (
          <TTUToolTip $isUs={isEmailLoginFlow}>
            <StyledH4 color="var(--color-text-primary)">
              {heading ||
                (isEmailLoginFlow
                  ? 'Have questions about the USMLE® Step 1 subscription?'
                  : 'Have questions about the subscription?')}
            </StyledH4>
            <StyledP2>Our expert can answer all your questions</StyledP2>
            <StyledButton
              label="Get a call from us"
              color="black"
              grid={5}
              onClick={() => {
                setShowTTUTooltip(false);
                sendTTUEvents(
                  TTU_POP_UP_CLICK,
                  {
                    user_ttu_status: isRepeatUser ? 'Repeat' : 'New',
                    last_primary_source: ttuTriggerSource,
                    last_primary_source_section: ttuTriggerSource,
                    ...eventProps
                  },
                  me.uid,
                  me.username,
                  selectedGoal.uid,
                  selectedGoal.name
                );
                if (selectedGoal.uid) {
                  setShowTTU(true);
                } else {
                  window.open(`/talktous?isOffline=${isOffline}`, '_blank');
                }
              }}
            />
            <CallDiv>
              <CallLabel>Or call us</CallLabel>
              <Phone
                href={`tel:${inboundCallNumber}`}
                onClick={onPhoneNumberClick}
              >
                <Call
                  size={Spacings.SPACING_5B}
                  viewBox="0 -4 24 24"
                  color="var(--color-text-primary)"
                />
                <H6>{inboundCallNumber}</H6>
              </Phone>
            </CallDiv>
            <TTUIllustrationContainer>
              <TTUIllustration src="ttu_illustration_new.svg" layout="fill" />
            </TTUIllustrationContainer>
          </TTUToolTip>
        )}
      </TTUWidget>
    );
  }
  return null;
}

export default TTUWidgetRenderer;
